import { Info, Volume2, VolumeX } from "lucide-react";
import { CancelIcon, ICON_SIZE } from "./actionIcons";
import { useState, useEffect } from "react";
import MockContextMenu from './MockContextMenu';

export default function CustomTooltip({ continuous, index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps }) {
    const showNextHint = step.disableNext;
    const showPrevHint = step.disablePrev;
    
    // Check if this step should show the mock context menu
    const showMockMenu = step.showMockContextMenu;
    const mockMenuProps = step.mockContextMenuProps || {};
    
    // Text-to-speech state
    const [isSpeaking, setIsSpeaking] = useState(false);

    const handleClose = (e) => {
        e.stopPropagation();
        // Stop any ongoing speech when closing
        if (isSpeaking) {
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
        if (skipProps && skipProps.onClick) {
            skipProps.onClick(e);
        } else if (closeProps && closeProps.onClick) {
            closeProps.onClick(e);
        }
    };

    const handleTextToSpeech = (e) => {
        e.stopPropagation();
        
        if (isSpeaking) {
            // Stop speaking
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        } else {
            // Start speaking
            const textToRead = `${step.title || ''}. ${step.content || ''}`;
            const utterance = new SpeechSynthesisUtterance(textToRead);
            
            // Configure speech
            utterance.rate = 0.9; // Slightly slower for clarity
            utterance.pitch = 1;
            utterance.volume = 1;
            
            // Handle end of speech
            utterance.onend = () => {
                setIsSpeaking(false);
            };
            
            utterance.onerror = () => {
                setIsSpeaking(false);
            };
            
            window.speechSynthesis.speak(utterance);
            setIsSpeaking(true);
        }
    };

    // Cleanup: stop speech when component unmounts or step changes
    useEffect(() => {
        return () => {
            if (isSpeaking) {
                window.speechSynthesis.cancel();
            }
        };
    }, [index]); // Reset when step changes

    return (
        <div 
            {...tooltipProps} 
            data-joyride-tooltip="true"
            className="p-6 rounded-3xl shadow-xl bg-white max-w-sm border border-gray-100 
                       backdrop-blur-sm relative overflow-hidden pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Gradient accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--theme-accent)]"></div>
            
            <div className="flex items-start justify-between gap-2 mb-3">
                {step.title && (
                    <h4 className="text-xl font-bold text-gray-900 leading-tight flex-1">
                        {step.title}
                    </h4>
                )}
                
                {/* Text-to-Speech Button */}
                {(step.title || step.content) && (
                    <button
                        onClick={handleTextToSpeech}
                        className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 ${
                            isSpeaking 
                                ? 'bg-[var(--theme-accent-tint)] text-[var(--theme-accent-ink)] hover:bg-[var(--theme-accent-tint)]' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={isSpeaking ? "Stop reading" : "Read aloud"}
                    >
                        {isSpeaking ? (
                            <VolumeX className="w-5 h-5" />
                        ) : (
                            <Volume2 className="w-5 h-5" />
                        )}
                    </button>
                )}
            </div>
            
            {step.content && (
                <div className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {step.content}
                </div>
            )}

            {/* Show mock context menu if specified */}
            {showMockMenu && (
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <MockContextMenu {...mockMenuProps} />
                </div>
            )}

            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    {backProps && (
                        <button
                            {...backProps}
                            onClick={(e) => {
                                e.stopPropagation();
                                backProps.onClick(e);
                            }}
                            disabled={step.disablePrev}
                            className="btn-t btn-t-secondary"
                        >
                            Back
                        </button>
                    )}
                    {continuous && (
                        <button
                            {...primaryProps}
                            onClick={(e) => {
                                e.stopPropagation();
                                primaryProps.onClick(e);
                            }}
                            disabled={step.disableNext}
                            className="btn-t btn-t-primary shadow-md"
                        >
                            Next
                        </button>
                    )}
                </div>
                
                <button 
                    onClick={handleClose}
                    className="btn-t btn-t-ghost"
                ><CancelIcon size={ICON_SIZE} />
                    Close
                </button>
            </div>

            {(showNextHint || showPrevHint) && (
                <div className="mt-2 p-2 bg-[var(--theme-accent-tint)] rounded-2xl border border-[var(--theme-accent-border)]">
                    <div className="flex items-center gap-2 text-xs text-[var(--theme-accent-ink)]">
                        <Info className="w-4 h-4 flex-shrink-0" />
                        <div>
                            {/* A step can name what it wants clicked; the
                                generic wording is the fallback for the many
                                steps written before that was possible. */}
                            {showNextHint && <span>{step.disableNextHint || 'Please click the required tab to continue forward.'}</span>}
                            {showPrevHint && <span>{step.disablePrevHint || 'Please click the required tab to go back.'}</span>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}